import { css } from '@emotion/react';
import { PaginatedList } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { useControlledField, TextField } from '@frontend/design-system';
import { useUserSearch } from '../../../hooks/use-user-search';
import { UsersSkeletonLoader } from '../../common/users-skeleton-loader/users-skeleton-loader';
import { UserListItem } from './user-list-item';

interface SelectMembersProps {
  selectedMemberIds: string[];
  onChange: (memberIds: string[]) => void;
}

export const MemberSelectionDropdown = ({ selectedMemberIds, onChange }: SelectMembersProps) => {
  const { t } = useTranslation('team-chat');
  const { onChangeSearchTerm, searchTerm, isLoading, usersToDisplay, filteredAllUsers } = useUserSearch();

  const textFieldProps = useControlledField({
    value: searchTerm,
    type: 'text',
    onChange: onChangeSearchTerm,
  });

  /** TODO: on large orgs this could benefit from using a fuse search */
  const addMember = async (userId: string) => {
    onChange([...selectedMemberIds, userId]);
  };

  const removeMember = async (userId: string) => {
    onChange(selectedMemberIds.filter((id) => id !== userId));
  };

  return (
    <>
      <TextField
        name=''
        label=''
        startAdornment={<Icon name='search' />}
        {...textFieldProps}
        placeholder={t('Search members')}
        clearable
      />
      <div css={userListContainerStyle}>
        <PaginatedList
          listData={usersToDisplay}
          emptyStateConfig={{
            header: !filteredAllUsers?.length
              ? t('Search for your co-workers by typing their name')
              : t('No members found'),
            type: 'generic',
          }}
          isLoading={isLoading}
          skeletonLoader={<UsersSkeletonLoader />}
          renderListItem={({ listItem: user }) => {
            const isMemberAdded = selectedMemberIds.includes(user.userID);
            return (
              <UserListItem
                user={user}
                isMemberAdded={isMemberAdded}
                removeMember={removeMember}
                addMember={addMember}
              />
            );
          }}
        />
      </div>
    </>
  );
};

const userListContainerStyle = css({
  overflowY: 'scroll',
  overflowX: 'hidden',
  height: '288px',
  boxShadow: theme.shadows.light,
  borderBottomLeftRadius: theme.borderRadius.medium,
  borderBottomRightRadius: theme.borderRadius.medium,
  marginBottom: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
});
